import { MRT_ColumnDef } from 'mantine-react-table'
import { Flex, Text } from '@mantine/core'
import { ReactNode } from 'react'
import { WireInstructionParams } from 'app/models/wire-instruction-params'
import Pill, { PillColor } from 'app/views/components/Table/Pill'
import WireInstructionActions from './wire-instruction-actions'
import { formatDateToShow, stringToDate } from 'app/utils/util-functions'
import WireInstructionHoverCard from 'app/views/components/popover/wire-instruction-hover-card'

enum StatusState {
  Active = 'Active',
  Unapproved = 'Unapproved',
  PendingKYC = 'Pending KYC',
  Inactive = 'Inactive',
  Expired = 'Expired',
  Scheduled = 'Scheduled',
}

const getPillColor = new Map<StatusState, PillColor>()
getPillColor.set(StatusState.Active, PillColor.GREEN)
getPillColor.set(StatusState.Unapproved, PillColor.PINK)
getPillColor.set(StatusState.PendingKYC, PillColor.ORANGE)
getPillColor.set(StatusState.Inactive, PillColor.PINK)
getPillColor.set(StatusState.Expired, PillColor.ORANGE)
getPillColor.set(StatusState.Scheduled, PillColor.PURPLE)


function TableHead({ header }: { header: string }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {header}
    </Text>
  )
}

function TableRowText({ text }: { text: ReactNode }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {text}
    </Text>
  )
}

export const wireInstructionCols = (businessDate: Date): MRT_ColumnDef<WireInstructionParams>[] => [
  {
    id: 'expandedIcon',
    header: '',
    size: 1,
    enableSorting: false,
    enableGrouping: false,
    enableHiding: false,
    Cell: ({ row }) =>
      row.depth > 0 ? (
        <WireInstructionHoverCard wireInstruction={row.original} fromWireInstructions={true} />
      ) : null,
  },
  {
    id: 'currency',
    size: 1,
    accessorKey: 'currency',
    header: '',
    Header: <TableHead header={''} />,
    Cell: ({ cell }) => <TableRowText text={cell.getValue<string>()} />,
    enableGrouping: true,
  },
  {
    size: 100,
    accessorKey: 'usedFor',
    header: 'Used For',
    Header: <TableHead header={'Used For'} />,
    Cell: ({ cell }) => {
      let items = cell.getValue<string[]>() || [];
      if (
        items.includes('TradesWI') &&
        items.includes('PrincipalInterestWI')
      ) {
        items = ['allPayments'];
      }

      if (items.includes('allPayments')) {
        items = ['allPayments'];
      }
      return (
        <Flex wrap="wrap" gap="10px">
	          {items.map((value: string) => {
            // human-readable label
            const label =
              value === 'TradesWI'
                ? 'Trades'
                : value === 'PrincipalInterestWI'
                  ? 'Principal/Interest'
                  : value === 'allPayments'
                    ? 'All Payments'
                    : value;

            return (
              <div
                key={value}
                style={{
                  display: 'flex',
                  padding: '6px 12px',
                  alignItems: 'center',
                  gap: '10px',
                  borderRadius: '5px',
                  background: '#E9F1FF',
                  color: '#757373',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                {label}
              </div>
            );
          })}
        </Flex>
      );
    },
  },
  {
    size: 100,
    accessorKey: 'defaultFor',
    header: 'Default For',
    Header: <TableHead header={'Default For'} />,
    Cell: ({ cell }) => {
      let items = cell.getValue<string[]>() || [];
      if (
        items.includes('TradesWI') &&
        items.includes('PrincipalInterestWI')
      ) {
        items = ['allPayments'];
      }

      if (items.includes('allPayments')) {
        items = ['allPayments'];
      }
      return (
        <Flex wrap="wrap" gap="10px">
	          {items.map((value: string) => {
            // human-readable label
            const label =
              value === 'TradesWI'
                ? 'Trades'
                : value === 'PrincipalInterestWI'
                  ? 'Principal/Interest'
                  : value === 'allPayments'
                    ? 'All Payments'
                    : value;

            return (
              <div
                key={value}
                style={{
                  display: 'flex',
                  padding: '6px 12px',
                  alignItems: 'center',
                  gap: '10px',
                  borderRadius: '5px',
                  background: '#E9F1FF',
                  color: '#757373',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                {label}
              </div>
            );
          })}
        </Flex>
      );
    },
  },
  {
    size: 100,
    accessorFn: (row) =>
      row.beneficiaryAccountNumber?.trim() ||
      row.creditorAccount?.trim() ||
      '',
    header: 'Account #',
    Header: <TableHead header={'Account #'} />,
    Cell: ({ cell }) => <TableRowText text={cell.getValue<string>()} />,
  },
  {
    size: 100,
    accessorKey: 'effectiveDate',
    header: 'Effective Date',
    Header: <TableHead header={'Effective Date'} />,
    accessorFn: row => {
      return row.effectiveDate ? stringToDate(row.effectiveDate || '') : null;
    },
    mantineFilterTextInputProps: {
      placeholder: 'Date',
    },
    filterFn: 'lessThanOrEqualTo',
    filterVariant: 'date',
    sortingFn: 'datetime',
    Cell: ({ cell }) => (
      <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
    ), //render Date as a string
  },
  {
    size: 100,
    accessorKey: 'endDate',
    header: 'Expiration Date',
    Header: <TableHead header={'Expiration Date'} />,
    accessorFn: row => {
      return row.endDate ? stringToDate(row.endDate || '') : null;
    },
    mantineFilterTextInputProps: {
      placeholder: 'Date',
    },
    filterFn: 'lessThanOrEqualTo',
    filterVariant: 'date',
    sortingFn: 'datetime',
    Cell: ({ cell }) => (
      <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
    ),
  },
  {
    size: 100,
    accessorKey: 'status',
    header: 'Status',
    Header: <TableHead header={'Status'} />,
    Cell: ({ cell, row }) => {
      const status = cell.getValue<string>() as StatusState;

      const endDate = row.original.endDate
        ? stringToDate(row.original.endDate)
        : null;
      const effectiveDate = row.original.effectiveDate
        ? stringToDate(row.original.effectiveDate)
        : null;

      let displayStatus = status;

      if (effectiveDate && businessDate < effectiveDate) {
        displayStatus = StatusState.Scheduled;
      } else if (endDate !== null && businessDate > endDate) {
        displayStatus = StatusState.Expired;
      }

      const color: PillColor =
        getPillColor.get(displayStatus) ?? PillColor.PINK;

      return <Pill text={displayStatus} color={color} />;
    },
  },
  {
    size: 100,
    accessorKey: 'id',
    header: 'Actions',
    Header: <TableHead header={'Actions'} />,
    enableColumnActions: false,
    enableColumnFilter: false,
    Cell: cell => <WireInstructionActions row={cell.row.original} />,
  },
]
